import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import AutoCompleteInput from '../AutocompleteInput';
import { useBooking } from '../../context/BookingContext';

export const BookingBar = () => {
  const navigate = useNavigate();

  const { bookingData, dispatch } = useBooking();

  const [locations, setLocations] = useState([]);

  const [origin, setStateOrigin] = useState('');
  const [destination, setStateDestination] = useState('');

  const setOrigin = value => {
    setStateOrigin(value);
    dispatch({
      type: 'UPDATE_FIELDS',
      payload: { pickup: value },
    });
  };

  const setDestination = value => {
    setStateDestination(value);
    dispatch({
      type: 'UPDATE_FIELDS',
      payload: { dropoff: value },
    });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    dispatch({
      type: 'UPDATE_FIELDS',
      payload: { [name]: value },
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    /*if (!formData.pickup || !formData.dropoff || !formData.date) {
      alert('Per favore compila tutti i campi obbligatori');
      return;
    }*/
    navigate('/booking');
  };

  React.useEffect(() => {
    const fetchEntries = async () => {
      try {
        const snapshotRoutes = await getDocs(collection(db, 'routes'));
        const locationsSet = new Set();
        snapshotRoutes.forEach(doc => {
          const data = doc.data();
          if (data.origin) locationsSet.add(data.origin);
          if (data.destination) locationsSet.add(data.destination);
        });

        setLocations(Array.from(locationsSet));
      } catch (err) {
        console.error(err);
      }
    };

    fetchEntries();
  }, []);

  return (
    <div className="inset-x-0 bottom-10 mx-auto w-full max-w-6xl px-4 md:relative md:bottom-auto">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col items-center gap-4 rounded-[2rem] border border-gray-100 bg-white p-4 shadow-2xl md:flex-row md:gap-0 md:p-2"
      >
        <div className="flex w-full items-center gap-4 border-b border-gray-100 px-4 pb-4 md:w-auto md:border-b-0 md:border-r md:pb-0">
          <div className="shrink-0 rounded-full bg-gray-100 p-3">
            <CalendarDaysIcon className="size-5 text-gray-600" />
          </div>
          <div className="flex flex-col">
            <label htmlFor="date" className="mb-1 block text-sm font-medium text-gray-900">
              Data di partenza
            </label>
            <input
              type="date"
              name="date"
              onChange={handleChange}
              id="date"
              value={bookingData.date}
              //min={getMinDateTime()}
              className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-800 shadow-sm focus:border-primary focus:ring-primary"
              required
            />
          </div>
        </div>

        <div className="flex w-full items-center gap-4 border-b border-gray-100 px-4 pb-4 md:w-auto md:border-b-0 md:border-r md:pb-0">
          <div className="shrink-0 rounded-full bg-gray-100 p-3">
            <ClockIcon className="size-5 text-gray-600" />
          </div>
          <div className="flex flex-col">
            <label htmlFor="time" className="mb-1 block text-sm font-medium text-gray-900">
              Ora di partenza
            </label>
            <input
              type="time"
              name="time"
              value={bookingData.time}
              onChange={handleChange}
              id="time"
              className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-800 shadow-sm focus:border-primary focus:ring-primary"
              required
            />
          </div>
        </div>

        <div className="flex w-full items-center gap-4 border-b border-gray-100 px-4 pb-4 md:flex-1 md:border-b-0 md:border-r md:pb-0">
          <div className="shrink-0 rounded-full bg-gray-100 p-3">
            <div className="flex size-5 items-center justify-center rounded-full border-2 border-gray-600">
              <div className="size-2 rounded-full bg-gray-600"></div>
            </div>
          </div>
          <div className="flex w-full flex-col">
            <AutoCompleteInput
              label="Partenza"
              name="origin"
              placeholder="Inserisci la partenza"
              value={origin}
              onChange={setOrigin}
              options={locations}
            />
          </div>
        </div>

        <div className="flex w-full items-center gap-4 px-4 pb-4 md:flex-1 md:pb-0">
          <div className="shrink-0 rounded-full bg-gray-100 p-3">
            <MapPinIcon className="size-5 text-gray-600" />
          </div>
          <div className="flex w-full flex-col">
            <AutoCompleteInput
              label="Destinazione"
              name="destination"
              placeholder="Inserisci la destinazione"
              value={destination}
              onChange={setDestination}
              options={locations}
            />
          </div>
        </div>

        <div className="w-full p-2 md:w-auto">
          <button
            type="submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-black px-8 py-4 font-medium text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-xl md:w-auto"
          >
            <MagnifyingGlassIcon className="size-5 stroke-2" />
            <span className="whitespace-nowrap">Show prices</span>
          </button>
        </div>
      </form>
    </div>
  );
};

const FieldGroup = ({ label, dot, children }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-1.5">
      {dot === 'from' && (
        <div className="size-2.5 shrink-0 rounded-full border-2 border-gray-400" />
      )}
      {dot === 'to' && <div className="size-2.5 shrink-0 rounded-full bg-gray-900" />}
      <span className="text-[10px] font-medium uppercase tracking-widest text-gray-400">
        {label}
      </span>
    </div>
    {children}
  </div>
);

const inputClass =
  'w-full rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-300 focus:border-gray-900 focus:bg-white';

export const BookingDialog = () => {
  const today = new Date().toISOString().split('T')[0];

  const { dispatch } = useBooking();

  const [form, setForm] = useState({
    depDate: today,
    depTime: '08:00',
    origin: '',
    destination: '',
    roundTrip: false,
    retDate: today,
    retTime: '18:00',
  });

  const setOrigin = value => {
    setForm(prev => ({ ...prev, origin: value }));
  };

  const setDestination = value => {
    setForm(prev => ({ ...prev, destination: value }));
  };

  const handleChange = field => e => {
    setForm(prev => {
      const updated = { ...prev, [field]: e.target.value };
      // se la data di partenza supera quella di ritorno, allineala
      if (field === 'depDate' && updated.retDate < e.target.value) {
        updated.retDate = e.target.value;
      }
      return updated;
    });
  };

  const toggleRoundTrip = () => {
    setForm(prev => ({ ...prev, roundTrip: !prev.roundTrip }));
  };

  const swapLocations = () => {
    setForm(prev => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin,
    }));
  };

  React.useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELDS',
      payload: { ...form },
    });
  }, [form, dispatch]);

  const navigate = useNavigate();

  const handleSubmit = e => {
    e.preventDefault();
    /*if (!form.origin || !form.destination) return;
    onSearch?.(form);*/
    navigate('/booking');
  };

  const [locations, setLocations] = useState([]);

  React.useEffect(() => {
    const fetchEntries = async () => {
      try {
        const snapshotRoutes = await getDocs(collection(db, 'routes'));
        const locationsSet = new Set();
        snapshotRoutes.forEach(doc => {
          const data = doc.data();
          if (data.origin) locationsSet.add(data.origin);
          if (data.destination) locationsSet.add(data.destination);
        });

        setLocations(Array.from(locationsSet));
      } catch (err) {
        console.error(err);
      }
    };

    fetchEntries();
  }, []);

  return (
    <>
      {/* Content */}
      <div className="relative z-10 w-full px-4 py-16 sm:px-6">
        <div className="mx-auto w-full max-w-xl">
          {/* Headline */}
          <p className="mb-2 text-xs uppercase tracking-[0.2em] text-white/40">
            Noleggio con conducente
          </p>
          <h1 className="mb-1 font-serif text-4xl leading-tight text-white sm:text-5xl">
            Il tuo viaggio, <em className="italic text-white/50">su misura.</em>
          </h1>
          <p className="mb-8 text-sm font-light tracking-wide text-white/35">
            Trasferimenti privati · Aeroporti · Eventi
          </p>

          {/* Booking card */}
          <form
            onSubmit={handleSubmit}
            className="rounded-[20px] bg-white p-5 shadow-2xl backdrop-blur"
          >
            {/* Data e ora di partenza */}
            <div className="mb-3 grid grid-cols-2 gap-2.5">
              <FieldGroup label="Data di partenza">
                <input
                  type="date"
                  required
                  name="depDate"
                  min={today}
                  value={form.depDate}
                  onChange={handleChange('depDate')}
                  className={inputClass}
                />
              </FieldGroup>
              <FieldGroup label="Ora di partenza">
                <input
                  type="time"
                  required
                  name="depTime"
                  value={form.depTime}
                  onChange={handleChange('depTime')}
                  className={inputClass}
                />
              </FieldGroup>
            </div>

            <hr className="my-3 border-gray-100" />

            {/* Partenza → Destinazione */}
            <div className="mb-3 grid grid-cols-[1fr_36px_1fr] items-end gap-2">
              <FieldGroup label="Partenza" dot="from">
                {/*<input
                  type="text"
                  required
                  placeholder="Città, aeroporto, indirizzo..."
                  autoComplete="off"
                  value={form.origin}
                  onChange={handleChange('origin')}
                  className={inputClass}
                />*/}
                <AutoCompleteInput
                  label=""
                  name="origin"
                  placeholder="Città, aeroporto, indirizzo..."
                  value={form.origin}
                  onChange={setOrigin}
                  options={locations}
                />
              </FieldGroup>

              {/* Swap button */}
              <button
                type="button"
                onClick={swapLocations}
                title="Inverti"
                className="mb-0 flex h-10 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-all hover:border-gray-900 hover:text-gray-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7h12m0 0-4-4m4 4-4 4M16 17H4m0 0 4 4m-4-4 4-4"
                  />
                </svg>
              </button>

              <FieldGroup label="Destinazione" dot="to">
                {/*<input
                  type="text"
                  required
                  placeholder="Città, aeroporto, indirizzo..."
                  autoComplete="off"
                  value={form.destination}
                  onChange={handleChange('destination')}
                  className={inputClass}
                />*/}
                <AutoCompleteInput
                  label=""
                  name="destination"
                  placeholder="Città, aeroporto, indirizzo..."
                  value={form.destination}
                  onChange={setDestination}
                  options={locations}
                />
              </FieldGroup>
            </div>

            {/* Checkbox andata e ritorno */}
            <label className="mb-3 flex cursor-pointer items-center gap-2.5">
              <button
                type="button"
                onClick={toggleRoundTrip}
                className={`
                  flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] transition-all ${form.roundTrip ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}`}
              >
                {form.roundTrip && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1 4L3.5 6.5L9 1"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
              <span className="select-none text-sm text-gray-600">Andata e ritorno</span>
            </label>

            {/* Sezione ritorno — animata */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                form.roundTrip ? 'mb-3 max-h-40 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {/* Separatore con label */}
              <div className="mb-2.5 flex items-center gap-2">
                <div className="h-px flex-1 bg-gray-100" />
                <span className="text-[10px] uppercase tracking-widest text-gray-400">Ritorno</span>
                <div className="h-px flex-1 bg-gray-100" />
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <FieldGroup label="Data di ritorno">
                  <input
                    type="date"
                    min={form.depDate}
                    value={form.retDate}
                    onChange={handleChange('retDate')}
                    className={inputClass}
                    required={form.roundTrip}
                  />
                </FieldGroup>
                <FieldGroup label="Ora di ritorno">
                  <input
                    type="time"
                    value={form.retTime}
                    onChange={handleChange('retTime')}
                    className={inputClass}
                    required={form.roundTrip}
                  />
                </FieldGroup>
              </div>

              {/* Info rotta invertita */}
              {form.destination && form.origin && (
                <p className="mt-2 text-xs text-gray-400">
                  Ritorno:{' '}
                  <span className="text-gray-600">
                    {form.destination} → {form.origin}
                  </span>
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 py-3.5 text-sm font-medium tracking-wide text-white transition-all hover:-translate-y-0.5 hover:bg-gray-700 active:translate-y-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="size-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <circle cx="11" cy="11" r="8" />
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
              </svg>
              Stima il prezzo
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
